import React, { useEffect, useRef, useState } from 'react';
import { LuMapPin, LuLoader, LuLocateFixed } from 'react-icons/lu';
import api from '../../services/api';
import { toast } from 'react-hot-toast';
import MapaLeaflet from './MapaLeaflet';

// Mapa en vivo del formulario (12-oct-2026): mientras se escribe la dirección muestra dónde cae.
// Si el punto no es el correcto, se toca el mapa en el lugar real y queda guardado a mano
// (mismo dato que usan el Mapa, la Rentabilidad y las rutas).
//
// Uso: <UbicacionEnMapa direccion={texto} onCambio={p => ...} />   p = { lat, lng, manual } | null
// Antes de guardar el lugar, si p.manual: guardarUbicacionManual(direccion, p).
// onDireccion (20-oct-2026): si se pasa, aparece "Usar mi ubicación": toma el GPS una sola vez
// (no sigue moviéndose), pone el punto y devuelve { calle, numero, localidad } para completar
// el formulario. Sirve cuando se carga el cliente estando en el lugar.
export async function guardarUbicacionManual(direccion, punto) {
    if (!punto?.manual || !direccion) return;
    try { await api.put('/mapa/ubicacion', { direccion, lat: punto.lat, lng: punto.lng }); } catch { /* el lugar igual se guarda */ }
}

// Dirección de un punto (OpenStreetMap / Nominatim, lo mismo que usa el mapa). Una consulta por toque.
async function direccionDelPunto(lat, lng) {
    const url = `https://nominatim.openstreetmap.org/reverse?format=jsonv2&addressdetails=1&zoom=18&accept-language=es&lat=${lat}&lon=${lng}`;
    const r = await fetch(url, { headers: { Accept: 'application/json' } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const a = (await r.json())?.address || {};
    const localidad = a.suburb || a.neighbourhood || a.city_district || a.town || a.village || a.city || a.municipality || '';
    const partido = a.city && a.city !== localidad ? a.city : (a.state_district || '');
    return {
        calle: a.road || a.pedestrian || '',
        numero: (a.house_number || '').split(/[;,]/)[0].trim(),
        localidad: [localidad, partido && !/^comuna/i.test(partido) ? partido : ''].filter(Boolean).join(', '),
    };
}

export default function UbicacionEnMapa({ direccion, onCambio, endpoint = '/mapa/buscar', onDireccion = null }) {
    const [punto, setPunto] = useState(null);   // { lat, lng, manual }
    const [estado, setEstado] = useState('');   // '' | buscando | ok | no | corto
    const ultima = useRef('');
    const cambio = useRef(onCambio);
    cambio.current = onCambio;
    // Después de "Usar mi ubicación" la dirección cambia sola: esa búsqueda no tiene que mover el punto del GPS
    const saltear = useRef(false);
    const [localizando, setLocalizando] = useState(false);

    useEffect(() => {
        const q = (direccion || '').trim();
        if (q.length < 6 || !/\d/.test(q)) { setEstado('corto'); return undefined; }
        if (q === ultima.current) return undefined;
        // Espera a que se deje de escribir (y no satura el buscador de mapas)
        const t = setTimeout(async () => {
            ultima.current = q;
            if (saltear.current) { saltear.current = false; return; }
            setEstado('buscando');
            try {
                const r = await api.get(endpoint, { params: { q } });
                if (r.data?.lat != null) {
                    const p = { lat: r.data.lat, lng: r.data.lng, manual: false, yaGuardadoAMano: !!r.data.manual };
                    setPunto(p); setEstado('ok'); cambio.current && cambio.current(p);
                } else { setEstado('no'); }
            } catch { setEstado('no'); }
        }, 1200);
        return () => clearTimeout(t);
    }, [direccion, endpoint]);

    const mover = (ll) => {
        const p = { lat: +ll.lat.toFixed(6), lng: +ll.lng.toFixed(6), manual: true };
        setPunto(p); setEstado('ok'); cambio.current && cambio.current(p);
    };

    const usarMiUbicacion = () => {
        if (!navigator.geolocation) { toast.error('Este dispositivo no da la ubicación'); return; }
        setLocalizando(true);
        navigator.geolocation.getCurrentPosition(async (pos) => {
            const p = { lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6), manual: true, gps: true };
            setPunto(p); setEstado('ok'); cambio.current && cambio.current(p);
            try {
                const d = await direccionDelPunto(p.lat, p.lng);
                if (d.calle || d.localidad) {
                    saltear.current = true; onDireccion(d);
                    setTimeout(() => { saltear.current = false; }, 4000);
                }
                else toast('Marcado en el mapa. Completá la dirección a mano.');
                if (pos.coords.accuracy > 150) toast('La ubicación es aproximada: revisá el número y el punto.');
            } catch {
                toast('Marcado en el mapa. No se pudo leer la dirección: completala a mano.');
            } finally { setLocalizando(false); }
        }, (err) => {
            setLocalizando(false);
            toast.error(err?.code === 1 ? 'No diste permiso de ubicación: cargá la dirección a mano' : 'No se pudo tomar la ubicación');
        }, { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 });
    };

    const texto = {
        corto: 'Escribí calle, número y localidad para ver dónde cae.',
        buscando: 'Buscando en el mapa…',
        ok: punto?.manual ? 'Marcado a mano: se guarda este punto.' : '¿No es ahí? Tocá el mapa en el lugar correcto.',
        no: 'No se encontró. Tocá el mapa en el lugar correcto para marcarlo.',
    }[estado] || '';
    const textoFinal = punto?.gps && estado !== 'buscando' ? 'Tu ubicación. Revisá el número; si no es ahí, tocá el lugar correcto.' : texto;

    return (
        <div className="space-y-1.5">
            {onDireccion && (
                <button type="button" onClick={usarMiUbicacion} disabled={localizando}
                    className="w-full h-10 flex items-center justify-center gap-1.5 rounded-xl bg-chip text-label font-black text-secondary active:scale-95 disabled:opacity-60">
                    {localizando ? <LuLoader size={14} className="animate-spin" /> : <LuLocateFixed size={14} />}
                    {localizando ? 'Buscando dónde estás…' : 'Usar mi ubicación'}
                </button>
            )}
            <div className="rounded-xl overflow-hidden border border-black/[0.07] dark:border-white/[0.07]">
                <MapaLeaflet puntos={punto ? [{ id: 'aca', lat: punto.lat, lng: punto.lng, color: punto.manual ? '#D48800' : '#C9341F' }] : []}
                    onClickMapa={mover} ajustarKey={punto && (!punto.manual || punto.gps) ? `${punto.lat},${punto.lng}` : 'fijo'} className="h-44" />
            </div>
            <p className={`text-label flex items-center gap-1 ${estado === 'no' && !punto?.gps ? 'text-[var(--warning-tx)] font-bold' : 'text-muted'}`}>
                {estado === 'buscando' ? <LuLoader size={12} className="animate-spin" /> : <LuMapPin size={12} />} {textoFinal}
            </p>
        </div>
    );
}
